import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Row, Col, Form, Spinner, Alert } from "react-bootstrap";
import api from "../../../services/api";
import { ResponsiveBar } from "@nivo/bar";
import Select from "react-select";
import { programaProp } from "../../../common/propTypes";

const MEDALS = ["🥇", "🥈", "🥉"];

const AssessorsRankingDashboard = ({ programaId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedProcedures, setSelectedProcedures] = useState([]);

  const [stateOptions, setStateOptions] = useState([]);
  const [procedureOptions, setProcedureOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const fetchData = useCallback(async () => {
    if (!programaId) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    if (error && !loadingOptions) setError(null);

    const params = new URLSearchParams();
    if (programaId) params.append("programa_id", programaId);
    if (selectedState) params.append("estado", selectedState.value);
    selectedProcedures.forEach(
      (proc) => proc?.value && params.append("procedimento_id", proc.value)
    );

    try {
      const response = await api.get(
        `/dashboards/ranking_assessores/?${params.toString()}`,
        {
          headers: {
            'X-Programa-ID': programaId
          }
        }
      );
      const formattedData = (response.data || [])
        .map((item) => ({
          assessor: item.assessor || "Sem nome",
          Procedimentos: Number(item.Procedimentos) || 0,
        }))
        .filter((item) => item.Procedimentos > 0);

      setData(formattedData);
    } catch (err) {
      console.error("Erro dashboard 'Ranking Assessores':", err);
      setError("Não foi possível carregar os dados do ranking.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [programaId, selectedState, selectedProcedures]);

  const fetchFilterOptions = useCallback(async () => {
    if (!programaId) {
      setStateOptions([]);
      setProcedureOptions([]);
      return;
    }
    setLoadingOptions(true);
    if (error && !loading) setError(null);

    const queryLimit = 1000;

    try {
      const [statesRes, proceduresRes] = await Promise.all([
        api.get(`/filters/estados/`),
        api.get(
          `/programas/servicos_procedimentos/?programa=${programaId}&status=true&limit=${queryLimit}`
        ),
      ]);

      setStateOptions(statesRes.data || []);
      setProcedureOptions(
        (proceduresRes.data.results || []).map((item) => ({
          value: item.servico_procedimento_id,
          label:
            item.dados_servico?.nome || `ID ${item.servico_procedimento_id}`,
        }))
      );
    } catch (err) {
      console.error("Erro ao buscar filtros:", err);
      setError("Erro ao carregar filtros.");
      setStateOptions([]);
      setProcedureOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  }, [programaId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const topThree = useMemo(() => data.slice(0, 3), [data]);

  return (
    <Card
      className="h-100 shadow-sm border-0"
      style={{
        borderRadius: "16px",
        background: "rgba(255,255,255,0.8)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Card.Header className="bg-transparent border-0 pb-0">
        <h5 className="fw-semibold text-dark mb-0">🏆 Ranking de Assessores</h5>
        <small className="text-muted">
          Procedimentos realizados por assessor
        </small>
      </Card.Header>

      <Card.Body className="d-flex flex-column mt-3">
        {/* Filtros */}
        <Row className="mb-3 gx-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="small fw-semibold">Estado (UF)</Form.Label>
              <Select
                options={stateOptions}
                value={selectedState}
                onChange={setSelectedState}
                placeholder="Todos"
                isClearable
                isLoading={loadingOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "12px",
                    borderColor: "#dee2e6",
                    minHeight: "40px",
                  }),
                }}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="small fw-semibold">
                Procedimentos
              </Form.Label>
              <Select
                options={procedureOptions}
                value={selectedProcedures}
                onChange={setSelectedProcedures}
                placeholder="Todos"
                isMulti
                isClearable
                isLoading={loadingOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "12px",
                    borderColor: "#dee2e6",
                    minHeight: "40px",
                  }),
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Gráfico */}
        <div
          className="flex-grow-1 d-flex align-items-center justify-content-center"
          style={{ minHeight: "320px" }}
        >
          {loading || loadingOptions ? (
            <Spinner animation="border" variant="primary" />
          ) : error ? (
            <Alert variant="danger" className="text-center">
              {error}
            </Alert>
          ) : data.length > 0 ? (
            <ResponsiveBar
              data={data}
              keys={["Procedimentos"]}
              indexBy="assessor"
              layout="horizontal"
              margin={{ top: 20, right: 50, bottom: 50, left: 130 }}
              padding={0.25}
              colors={{ scheme: "paired" }}
              borderColor={{ from: "color", modifiers: [["darker", 1.2]] }}
              axisBottom={{
                legend: "Procedimentos",
                legendPosition: "middle",
                legendOffset: 40,
                tickSize: 5,
                tickPadding: 5,
              }}
              axisLeft={{
                tickPadding: 10,
              }}
              enableGridY={false}
              enableGridX={true}
              labelTextColor={{ from: "color", modifiers: [["darker", 2]] }}
              tooltip={({ indexValue, value }) => (
                <div className="p-2 bg-white rounded shadow-sm">
                  <strong>{indexValue}</strong>
                  <br />
                  {value} procedimentos
                </div>
              )}
              motionConfig="gentle"
            />
          ) : (
            <p className="text-muted">Nenhum dado encontrado.</p>
          )}
        </div>

        {/* Top 3 */}
        {!loading && !error && topThree.length > 0 && (
          <div className="mt-4 border-top pt-3">
            <h6 className="fw-semibold mb-3 text-dark">Top 3 Assessores</h6>
            {topThree.map((a, i) => (
              <div
                key={a.assessor}
                className="d-flex justify-content-between align-items-center p-3 rounded-4"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(240,240,240,0.9), rgba(250,250,250,0.9))",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <span className="fs-2">{MEDALS[i]}</span>
                  <div>
                    <p className="fw-semibold mb-0">{a.assessor}</p>
                    <small className="text-muted">{i + 1}º lugar</small>
                  </div>
                </div>
                <div className="text-end">
                  <h5 className="text-primary fw-bold mb-0">
                    {a.Procedimentos}
                  </h5>
                  <small className="text-muted">
                    {a.Procedimentos === 1 ? "procedimento" : "procedimentos"}
                  </small>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

AssessorsRankingDashboard.propTypes = {
  ...programaProp,
};

export default AssessorsRankingDashboard;
